/**
 * ThesisDraftSheet — bottom-anchored editor for the live thesis card (spec
 * §3.6). Every field commit calls onEdit with a single-field patch; the parent
 * applies it optimistically and POSTs it as a structured thesis_edit turn.
 *
 * Deviation 2 (plan): drag-to-snap is out of scope — a plain RN Modal with a
 * grab handle, no new dependency. Strategy labels reuse the portfolio.duration*
 * i18n keys (Core/Growth/Speculative); it is the only strategy question —
 * conviction left the builder flow (controller ruling).
 *
 * List/price fields keep local drafts, re-seeded when the sheet opens: the card
 * prop round-trips through the server, so binding inputs straight to it would
 * fire a POST per keystroke and yank rows out from under the user. The next
 * explicit commit wins (last-writer-wins).
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Modal,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import { DatePicker } from '../ui/DatePicker';
import { LongTextField } from '../ui/LongTextField';
import { saveEnabled } from '../../services/thesisCard';
import type { ThesisCard } from '../../services/chatStream';

const STRATEGY_OPTIONS = [
  { value: 'Core', labelKey: 'portfolio.durationCoreHolding', defaultLabel: 'Core' },
  { value: 'Growth', labelKey: 'portfolio.durationMidTerm', defaultLabel: 'Growth' },
  { value: 'Speculative', labelKey: 'portfolio.durationShortTerm', defaultLabel: 'Speculative' },
] as const;

type PriceField = 'entry_price' | 'stop_loss_price' | 'take_profit_price';
const LIST_MAX = 10;

function priceToDraft(v: number | null | undefined): string {
  return v != null ? String(v) : '';
}

export function ThesisDraftSheet({
  visible,
  card,
  onClose,
  onEdit,
  onSave,
  saving,
}: {
  visible: boolean;
  card: ThesisCard | null;
  onClose: () => void;
  onEdit: (fields: Partial<ThesisCard>) => void;
  onSave: () => void;
  saving: boolean;
}) {
  const { theme } = useTheme();
  const { t } = useTranslation();
  const styles = useMemo(() => createThemedStyles(theme), [theme]);

  // Local drafts for the free-typed fields; re-seeded each time the sheet
  // opens (deliberately not while open — see the file comment).
  const [pillarsDraft, setPillarsDraft] = useState<string[]>([]);
  const [risksDraft, setRisksDraft] = useState<string[]>([]);
  const [entryDraft, setEntryDraft] = useState('');
  const [stopDraft, setStopDraft] = useState('');
  const [takeDraft, setTakeDraft] = useState('');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!visible) return;
    setPillarsDraft(card?.pillars ?? []);
    setRisksDraft(card?.risks ?? []);
    setEntryDraft(priceToDraft(card?.entry_price));
    setStopDraft(priceToDraft(card?.stop_loss_price));
    setTakeDraft(priceToDraft(card?.take_profit_price));
  }, [visible]);

  const canSave = card ? saveEnabled(card) : false;

  const patchPillars = (next: string[]) => {
    setPillarsDraft(next);
    onEdit({ pillars: next.map((s) => s.trim()).filter(Boolean) });
  };

  const patchRisks = (next: string[]) => {
    setRisksDraft(next);
    onEdit({ risks: next.map((s) => s.trim()).filter(Boolean) });
  };

  const commitPrice = (field: PriceField, draft: string) => {
    if (!card) return;
    const s = draft.trim();
    if (!s) {
      if (card[field] != null) onEdit({ [field]: null });
      return;
    }
    const n = Number(s);
    if (!Number.isFinite(n) || n < 0) return; // ignore junk; keep the last good value
    if (card[field] === n) return;
    onEdit({ [field]: n });
  };

  return (
    <Modal visible={visible} animationType="slide" transparent onRequestClose={onClose}>
      <View style={styles.backdrop}>
        <Pressable
          style={styles.backdropDismiss}
          onPress={onClose}
          accessibilityRole="button"
          accessibilityLabel={t('common.close', { defaultValue: 'Close' })}
        />
        <View style={[styles.sheet, { backgroundColor: theme.colors.surface }]}>
          <View style={[styles.handle, { backgroundColor: theme.colors.border }]} />
          {card && (
            <>
              <Text style={[styles.title, { color: theme.colors.text }]}>
                {t('chat.thesisDraft', { defaultValue: 'Thesis draft' })} · {card.symbol}
              </Text>

              <ScrollView
                style={styles.scroll}
                contentContainerStyle={styles.scrollContent}
                keyboardShouldPersistTaps="handled"
              >
                {/* Strategy — Core/Growth/Speculative (portfolio.duration* labels) */}
                <Text style={[styles.label, { color: theme.colors.textSecondary }]}>
                  {t('portfolio.convictionStrategy', { defaultValue: 'Conviction Strategy' })}
                </Text>
                <View style={styles.optionRow}>
                  {STRATEGY_OPTIONS.map((option) => {
                    const selected = card.duration === option.value;
                    return (
                      <TouchableOpacity
                        key={option.value}
                        testID={`thesis-strategy-${option.value}`}
                        style={[
                          styles.option,
                          {
                            borderColor: selected ? theme.colors.primary : theme.colors.border,
                            backgroundColor: selected
                              ? theme.colors.selectedBackground
                              : theme.colors.backgroundElevated,
                          },
                        ]}
                        onPress={() => onEdit({ duration: selected ? null : option.value })}
                        disabled={saving}
                        accessibilityRole="button"
                        accessibilityState={{ selected }}
                      >
                        <Text
                          style={[
                            styles.optionText,
                            { color: selected ? theme.colors.primary : theme.colors.text },
                          ]}
                        >
                          {t(option.labelKey, { defaultValue: option.defaultLabel })}
                        </Text>
                      </TouchableOpacity>
                    );
                  })}
                </View>

                {/* Statement */}
                <Text style={[styles.label, { color: theme.colors.textSecondary }]}>
                  {t('portfolio.thesisStatement', { defaultValue: 'Thesis Statement' })}
                </Text>
                <LongTextField
                  testID="thesis-statement"
                  value={card.statement ?? ''}
                  onChangeText={(text) => onEdit({ statement: text })}
                  placeholder={t('portfolio.thesisStatementHint', {
                    defaultValue: 'Summarize your investment thesis in one paragraph.',
                  })}
                  modalTitle={t('portfolio.thesisStatement', { defaultValue: 'Thesis Statement' })}
                  maxLength={1000}
                  editable={!saving}
                />

                {/* Pillars */}
                <Text style={[styles.label, { color: theme.colors.textSecondary }]}>
                  {t('portfolio.keyPillars', { defaultValue: 'Key Pillars' })}
                </Text>
                {pillarsDraft.map((pillar, index) => (
                  <View key={`pillar-${index}`} style={styles.listRow}>
                    <View style={styles.listInput}>
                      <LongTextField
                        testID={`thesis-pillar-${index}`}
                        value={pillar}
                        onChangeText={(text) => {
                          const next = [...pillarsDraft];
                          next[index] = text;
                          patchPillars(next);
                        }}
                        placeholder={t('portfolio.pillarPlaceholder', {
                          defaultValue: `e.g., 'AI market leadership'`,
                        })}
                        modalTitle={t('portfolio.editPillar', { defaultValue: 'Edit Pillar' })}
                        maxLength={200}
                        editable={!saving}
                      />
                    </View>
                    <TouchableOpacity
                      testID={`thesis-remove-pillar-${index}`}
                      style={[styles.removeButton, { borderColor: theme.colors.error }]}
                      onPress={() => patchPillars(pillarsDraft.filter((_, i) => i !== index))}
                      disabled={saving}
                    >
                      <Ionicons name="close-circle" size={20} color={theme.colors.error} />
                    </TouchableOpacity>
                  </View>
                ))}
                {pillarsDraft.length < LIST_MAX && (
                  <TouchableOpacity
                    testID="thesis-add-pillar"
                    style={[styles.addButton, { borderColor: theme.colors.primary }]}
                    onPress={() => setPillarsDraft([...pillarsDraft, ''])}
                    disabled={saving}
                  >
                    <Ionicons name="add-circle" size={20} color={theme.colors.primary} />
                    <Text style={[styles.addButtonText, { color: theme.colors.primary }]}>
                      {t('portfolio.addPillar', { defaultValue: 'Add Pillar' })}
                    </Text>
                  </TouchableOpacity>
                )}

                {/* Risks */}
                <Text style={[styles.label, { color: theme.colors.textSecondary }]}>
                  {t('portfolio.keyRisks', { defaultValue: 'Key Risks' })}
                </Text>
                {risksDraft.map((risk, index) => (
                  <View key={`risk-${index}`} style={styles.listRow}>
                    <View style={styles.listInput}>
                      <LongTextField
                        testID={`thesis-risk-${index}`}
                        value={risk}
                        onChangeText={(text) => {
                          const next = [...risksDraft];
                          next[index] = text;
                          patchRisks(next);
                        }}
                        placeholder={t('portfolio.riskPlaceholder', {
                          defaultValue: `e.g., 'China export restrictions'`,
                        })}
                        modalTitle={t('portfolio.editRisk', { defaultValue: 'Edit Risk' })}
                        maxLength={200}
                        editable={!saving}
                      />
                    </View>
                    <TouchableOpacity
                      testID={`thesis-remove-risk-${index}`}
                      style={[styles.removeButton, { borderColor: theme.colors.error }]}
                      onPress={() => patchRisks(risksDraft.filter((_, i) => i !== index))}
                      disabled={saving}
                    >
                      <Ionicons name="close-circle" size={20} color={theme.colors.error} />
                    </TouchableOpacity>
                  </View>
                ))}
                {risksDraft.length < LIST_MAX && (
                  <TouchableOpacity
                    testID="thesis-add-risk"
                    style={[styles.addButton, { borderColor: theme.colors.primary }]}
                    onPress={() => setRisksDraft([...risksDraft, ''])}
                    disabled={saving}
                  >
                    <Ionicons name="add-circle" size={20} color={theme.colors.primary} />
                    <Text style={[styles.addButtonText, { color: theme.colors.primary }]}>
                      {t('portfolio.addRisk', { defaultValue: 'Add Risk' })}
                    </Text>
                  </TouchableOpacity>
                )}

                {/* Prices + reminder */}
                <View style={styles.fieldRow}>
                  <View style={styles.halfField}>
                    <Text style={[styles.label, { color: theme.colors.textSecondary }]}>
                      {t('watchlist.entryPrice', { defaultValue: 'Entry price' })}
                    </Text>
                    <TextInput
                      testID="thesis-entry-price"
                      style={[
                        styles.input,
                        {
                          backgroundColor: theme.colors.backgroundElevated,
                          borderColor: theme.colors.border,
                          color: theme.colors.text,
                        },
                      ]}
                      value={entryDraft}
                      onChangeText={setEntryDraft}
                      onEndEditing={() => commitPrice('entry_price', entryDraft)}
                      keyboardType="decimal-pad"
                      placeholder="0.00"
                      placeholderTextColor={theme.colors.textTertiary}
                      editable={!saving}
                    />
                  </View>
                  <View style={styles.halfField}>
                    <Text style={[styles.label, { color: theme.colors.textSecondary }]}>
                      {t('watchlist.reminderDate', { defaultValue: 'Reminder date' })}
                    </Text>
                    <DatePicker
                      testID="thesis-remind-at"
                      value={card.remind_at ?? null}
                      onChange={(date) => onEdit({ remind_at: date })}
                    />
                  </View>
                </View>
                <View style={styles.fieldRow}>
                  <View style={styles.halfField}>
                    <Text style={[styles.label, { color: theme.colors.textSecondary }]}>
                      {t('portfolio.stopLoss', { defaultValue: 'Stop Loss' })}
                    </Text>
                    <TextInput
                      testID="thesis-stop-loss"
                      style={[
                        styles.input,
                        {
                          backgroundColor: theme.colors.backgroundElevated,
                          borderColor: theme.colors.border,
                          color: theme.colors.text,
                        },
                      ]}
                      value={stopDraft}
                      onChangeText={setStopDraft}
                      onEndEditing={() => commitPrice('stop_loss_price', stopDraft)}
                      keyboardType="decimal-pad"
                      placeholder="0.00"
                      placeholderTextColor={theme.colors.textTertiary}
                      editable={!saving}
                    />
                  </View>
                  <View style={styles.halfField}>
                    <Text style={[styles.label, { color: theme.colors.textSecondary }]}>
                      {t('portfolio.takeProfit', { defaultValue: 'Take Profit' })}
                    </Text>
                    <TextInput
                      testID="thesis-take-profit"
                      style={[
                        styles.input,
                        {
                          backgroundColor: theme.colors.backgroundElevated,
                          borderColor: theme.colors.border,
                          color: theme.colors.text,
                        },
                      ]}
                      value={takeDraft}
                      onChangeText={setTakeDraft}
                      onEndEditing={() => commitPrice('take_profit_price', takeDraft)}
                      keyboardType="decimal-pad"
                      placeholder="0.00"
                      placeholderTextColor={theme.colors.textTertiary}
                      editable={!saving}
                    />
                  </View>
                </View>
              </ScrollView>

              {/* Footer: close + confirm-to-save (enabled iff the card can save) */}
              <View style={styles.footer}>
                <TouchableOpacity
                  testID="thesis-sheet-close"
                  style={[styles.closeButton, { borderColor: theme.colors.border }]}
                  onPress={onClose}
                  disabled={saving}
                >
                  <Text style={[styles.closeButtonText, { color: theme.colors.textSecondary }]}>
                    ✕ {t('common.close', { defaultValue: 'Close' })}
                  </Text>
                </TouchableOpacity>
                <TouchableOpacity
                  testID="thesis-sheet-save"
                  style={[
                    styles.saveButton,
                    { backgroundColor: theme.colors.primary },
                    !canSave && styles.saveDisabled,
                  ]}
                  onPress={onSave}
                  disabled={!canSave || saving}
                  accessibilityRole="button"
                  accessibilityState={{ disabled: !canSave || saving }}
                >
                  <Text style={[styles.saveButtonText, { color: theme.colors.textInverse }]}>
                    {saving
                      ? t('common.saving', { defaultValue: 'Saving...' })
                      : t('chat.saveThesis', { defaultValue: 'Save thesis' })}
                  </Text>
                </TouchableOpacity>
              </View>
            </>
          )}
        </View>
      </View>
    </Modal>
  );
}

function createThemedStyles(theme: any) {
  return StyleSheet.create({
    backdrop: {
      flex: 1,
      justifyContent: 'flex-end',
      backgroundColor: theme.colors.overlay,
    },
    backdropDismiss: {
      ...StyleSheet.absoluteFillObject,
    },
    sheet: {
      maxHeight: '90%',
      borderTopLeftRadius: theme.radii.lg,
      borderTopRightRadius: theme.radii.lg,
      paddingTop: theme.spacing.xs,
      paddingBottom: theme.spacing.lg,
    },
    handle: {
      alignSelf: 'center',
      width: 40,
      height: 4,
      borderRadius: theme.radii.full,
      marginBottom: theme.spacing.sm,
    },
    title: {
      fontSize: theme.typography.fontSizeLg,
      fontWeight: theme.typography.fontWeightBold,
      paddingHorizontal: theme.spacing.lg,
      marginBottom: theme.spacing.sm,
    },
    // Shrink below content height so the sheet respects its maxHeight cap.
    scroll: {
      flexGrow: 0,
      flexShrink: 1,
    },
    scrollContent: {
      paddingHorizontal: theme.spacing.lg,
      gap: theme.spacing.xs,
      paddingBottom: theme.spacing.md,
    },
    label: {
      fontSize: theme.typography.fontSizeSm,
      fontWeight: theme.typography.fontWeightSemibold,
      marginTop: theme.spacing.sm,
      marginBottom: theme.spacing.xxs,
    },
    optionRow: {
      flexDirection: 'row',
      gap: theme.spacing.sm,
    },
    option: {
      flex: 1,
      alignItems: 'center',
      paddingVertical: theme.spacing.sm,
      paddingHorizontal: theme.spacing.xs,
      borderRadius: theme.radii.md,
      borderWidth: theme.id === 'bauhaus' ? 2 : 1,
    },
    optionText: {
      fontSize: theme.typography.fontSizeSm,
      fontWeight: theme.typography.fontWeightSemibold,
    },
    listRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: theme.spacing.sm,
      marginTop: theme.spacing.xxs,
    },
    listInput: {
      flex: 1,
      minWidth: 0,
    },
    removeButton: {
      padding: theme.spacing.sm,
      borderWidth: theme.id === 'bauhaus' ? 2 : 1,
      borderRadius: theme.radii.sm,
      alignItems: 'center',
      justifyContent: 'center',
    },
    addButton: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: theme.spacing.xs,
      paddingVertical: theme.spacing.sm,
      paddingHorizontal: theme.spacing.md,
      borderRadius: theme.radii.md,
      borderWidth: theme.id === 'bauhaus' ? 2 : 1,
      marginTop: theme.spacing.xs,
    },
    addButtonText: {
      fontSize: theme.typography.fontSizeSm,
      fontWeight: theme.typography.fontWeightSemibold,
    },
    fieldRow: {
      flexDirection: 'row',
      gap: theme.spacing.md,
      marginTop: theme.spacing.sm,
    },
    halfField: {
      flex: 1,
      minWidth: 0,
    },
    input: {
      borderWidth: theme.id === 'bauhaus' ? 2 : 1,
      borderRadius: theme.radii.md,
      paddingHorizontal: theme.spacing.md,
      paddingVertical: theme.spacing.sm,
      fontSize: theme.typography.fontSizeMd,
      minWidth: 0,
    },
    footer: {
      flexDirection: 'row',
      gap: theme.spacing.md,
      paddingHorizontal: theme.spacing.lg,
      paddingTop: theme.spacing.md,
    },
    closeButton: {
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: 48,
      borderRadius: theme.radii.md,
      borderWidth: theme.id === 'bauhaus' ? 2 : 1,
    },
    closeButtonText: {
      fontSize: theme.typography.fontSizeMd,
      fontWeight: theme.typography.fontWeightSemibold,
    },
    saveButton: {
      flex: 2,
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: 48,
      borderRadius: theme.radii.md,
      borderWidth: theme.id === 'bauhaus' ? 2 : 0,
      borderColor: theme.colors.primary,
    },
    saveDisabled: {
      opacity: 0.5,
    },
    saveButtonText: {
      fontSize: theme.typography.fontSizeMd,
      fontWeight: theme.typography.fontWeightSemibold,
    },
  });
}
